import React, { useState } from "react"
import { usePage, Link } from "@inertiajs/react"
import { useIsMobile } from "@/hooks/use-mobile"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Toaster } from "@/components/ui/sonner"
import {
  Activity,
  Bell,
  CreditCard,
  History,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  Settings,
  Store,
  Timer,
  UserPen,
  Users,
  X,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { BrandLockup } from "@/components/ui/brand-logo"
import { t } from "@/lib/i18n"

type SharedUser = {
  name: string
  email: string
  initials: string
  avatar: string | null
}

const NAV_ITEMS = [
  { titleKey: "ui.navigation.home", href: "/", icon: LayoutDashboard },
  { titleKey: "ui.navigation.locations", href: "/locations", icon: Store },
  { titleKey: "ui.navigation.transactions", href: "/transactions", icon: CreditCard },
  { titleKey: "ui.navigation.users", href: "/users", icon: Users },
  { titleKey: "ui.navigation.events", href: "/events", icon: History },
  { titleKey: "ui.navigation.jobs", href: "/jobs", icon: Timer },
  { titleKey: "ui.navigation.monitoring", href: "/status", icon: Activity },
]

function NavItem({
  item,
  active,
  onClick,
}: {
  item: (typeof NAV_ITEMS)[number]
  active?: boolean
  onClick?: () => void
}) {
  return (
    <a
      href={item.href}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
        active
          ? "bg-muted text-foreground"
          : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
      )}
    >
      <item.icon className="h-4 w-4 shrink-0" />
      {t(item.titleKey)}
    </a>
  )
}

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const { url } = usePage()

  return (
    <div className="flex h-full flex-col">
      <nav className="flex flex-col gap-1 px-3 pt-2">
        {NAV_ITEMS.map((item) => (
          <NavItem
            key={t(item.titleKey)}
            item={item}
            active={item.href === "/" ? url === "/" : url.startsWith(item.href)}
            onClick={onNavigate}
          />
        ))}
      </nav>
    </div>
  )
}

function UserMenu({ user }: { user: SharedUser }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="size-9 rounded-full">
          <Avatar className="size-8">
            <AvatarImage src={user.avatar || ""} alt={user.name} />
            <AvatarFallback className="text-xs">{user.initials}</AvatarFallback>
          </Avatar>
          <span className="sr-only">{t("ui.navigation.account_menu")}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="truncate text-sm font-medium">{user.name}</span>
          <span className="truncate text-xs font-normal text-muted-foreground">
            {user.email}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/profile/edit">
            <UserPen />
            {t("ui.react.components.features.profiles.profile_sidebar.edit_profile")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/profile/password">
            <KeyRound />
            {t("ui.react.components.features.profiles.profile_sidebar.change_password")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/profile/settings">
            <Settings />
            {t("ui.react.components.features.profiles.profile_sidebar.configuration")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild variant="destructive">
          <Link href="/session" method="delete" as="button" className="w-full">
            <LogOut />
            {t("ui.react.components.features.profiles.profile_sidebar.close_session")}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const { user } = usePage<{ user: SharedUser | null }>().props
  const isMobile = useIsMobile()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  return (
    <div className="flex h-screen flex-col">
      {/* ─── Top Navbar ─── */}
      <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center justify-between bg-background px-4">
        <div className="flex items-center gap-3">
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
              <span className="sr-only">{t("ui.navigation.open_menu")}</span>
            </Button>
          )}
          <a href="/" className="hidden items-center gap-2 md:flex mt-2">
            <BrandLockup className="ml-1" />
          </a>
        </div>

        <div className="flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative size-9">
                <Bell className="h-[18px] w-[18px]" />
                <span className="sr-only">{t("ui.navigation.notifications")}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="px-4 py-3 text-sm font-semibold">
                {t("ui.navigation.notifications")}
              </div>
              <DropdownMenuSeparator />
              <div className="p-6 text-center text-sm text-muted-foreground">
                {t("ui.navigation.no_new_notifications")}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {user && <UserMenu user={user} />}
        </div>
      </header>

      {/* ─── Body: Sidebar + Content ─── */}
      <div className="flex flex-1 overflow-hidden">
        {/* Desktop sidebar */}
        {!isMobile && (
          <aside className="hidden w-56 shrink-0 md:block">
            <SidebarNav />
          </aside>
        )}

        {/* Mobile sidebar (sheet) */}
        {isMobile && (
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetContent side="left" className="w-64 p-0" showCloseButton={false}>
              <SheetHeader className="sr-only">
                <SheetTitle>{t("ui.navigation.navigation_menu")}</SheetTitle>
              </SheetHeader>
              <div className="flex h-14 items-center justify-between px-4">
                <a href="/" className="flex items-center gap-2">
                  <BrandLockup className="ml-1" />
                </a>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={() => setSidebarOpen(false)}
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">{t("ui.navigation.close_menu")}</span>
                </Button>
              </div>
              <SidebarNav onNavigate={() => setSidebarOpen(false)} />
            </SheetContent>
          </Sheet>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto">
          {children}
        </div>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  )
}
